import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { logger } from 'src/analytics/KatalLogger';

/**
 * Service for validating headers in forecast templates
 */
export class HeaderValidationService extends ValidationService {
  /**
   * Validates that the headers match the expected headers in both presence and order.
   * @param headerRow The list of headers from the uploaded file.
   * @param expectedHeaders The expected list of headers.
   * @returns Promise<ValidationStatusEntity> The validation status of the headers.
   */
  public async validateHeaders(headerRow: string[], expectedHeaders: string[]): Promise<ValidationStatusEntity> {
    const validationErrorDetails: ValidationErrorDetail[] = [];

    // Check if header count matches
    if (headerRow.length !== expectedHeaders.length) {
      validationErrorDetails.push({
        message: `Header count mismatch. Expected ${expectedHeaders.length} headers, found ${headerRow.length}.`
      });
    } else {
      // Check each header
      headerRow.forEach((header, rowIndex) => {
        if (header !== expectedHeaders[rowIndex]) {
          validationErrorDetails.push({
            rowIndex: rowIndex + 1,
            message: `Header mismatch. Expected '${expectedHeaders[rowIndex]}', found '${header}'.`
          });
        }
      });
    }

    // Log validation errors if any
    this.logValidationErrors('Header', validationErrorDetails);

    // Return appropriate validation status
    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(
        ForecastValidationMessages.HEADER_VALIDATION_FAILED,
        ForecastValidationMessages.HEADER_VALIDATION_DEFAULT_MESSAGE,
        validationErrorDetails
      );
    }

    return this.createSuccessStatus(
      ForecastValidationMessages.HEADER_VALIDATION_SUCCESS,
      ForecastValidationMessages.HEADER_VALIDATION_DEFAULT_MESSAGE
    );
  }
}

// Export singleton instance
export const headerValidationService = new HeaderValidationService();
